'use client';

/**
 * Warm the "You" panel's data while the guest is still reading the menu.
 *
 * THE PROBLEM. `CustomerAccountAffordance` fetches loyalty links, gift cards and
 * order history in a `useEffect` on mount — and it only mounts when the sheet
 * opens, because Radix unmounts a closed sheet's children. So the guest tapped
 * "You" and then watched three sequential round trips finish before the panel
 * had anything to show. On café Wi-Fi that is seconds of blank sheet, every
 * time, for data that was knowable the moment the page loaded.
 *
 * THE FIX, and why a cache rather than props. Prefetching is a page-level
 * concern and rendering is a sheet-level one; threading four pieces of state
 * through a 627-line component would couple them together and would have to be
 * repeated for the next sheet. Instead the page calls `useCustomerAccountPrefetch`
 * (always mounted, fires once) and the panel reads whatever is already there via
 * `readAccountCache`, then revalidates quietly. First paint is instant when the
 * prefetch has landed and no worse than before when it has not.
 *
 * Scoped by businessId: the same guest on two different merchants' menus must
 * not see one merchant's cache on the other's page.
 */

import { useEffect } from 'react';

import {
  invalidatePersistedPrefix,
  peekPersisted,
  readPersisted,
  writePersisted as writePersistedCache,
} from '@/lib/client/persisted-cache';

export type AccountSnapshot = {
  linked: unknown[];
  candidates: unknown[];
  giftCards: unknown[];
  orders: unknown[];
  /** When this snapshot was taken, so a stale one can be revalidated. */
  at: number;
};

/** Snapshots older than this are refetched rather than merely shown. */
const MAX_AGE_MS = 60_000;

function keyFor(businessId: string | null | undefined) {
  return `account:${businessId || '__all__'}`;
}

/**
 * A snapshot at ANY age, for painting.
 *
 * This used to drop anything past `MAX_AGE_MS`, which meant a guest who reloaded
 * the menu page — the single most common thing a guest does with a QR menu, and
 * the whole reason a cache exists — got a blank sheet again, because the cache
 * lived in a `Map` that the reload had already destroyed. Age now decides only
 * whether a REFRESH follows the paint, never whether the paint happens.
 */
export function readAccountCache(businessId: string | null | undefined): AccountSnapshot | null {
  const hit = peekPersisted<Omit<AccountSnapshot, 'at'>>(keyFor(businessId));
  if (!hit) return null;
  return { ...hit.value, at: hit.at };
}

/** True when the painted snapshot is old enough to be worth refreshing. */
export function isAccountCacheStale(businessId: string | null | undefined): boolean {
  const hit = readPersisted<Omit<AccountSnapshot, 'at'>>(keyFor(businessId), MAX_AGE_MS);
  return !hit;
}

export function writeAccountCache(
  businessId: string | null | undefined,
  snapshot: Omit<AccountSnapshot, 'at'>,
) {
  writePersistedCache(keyFor(businessId), snapshot);
}

/** Dropped on sign-out, so the next guest on this device starts clean. */
export function clearAccountCache() {
  invalidatePersistedPrefix('account:');
}

/**
 * Called once, high in the page. Does nothing until the guest is signed in —
 * there is no account data to fetch for an anonymous visitor, and firing these
 * requests anyway is how the public menu ended up with a stream of 401s.
 */
export function useCustomerAccountPrefetch(input: {
  enabled: boolean;
  businessId: string | null | undefined;
  fetcher: (path: string, init?: RequestInit) => Promise<Response>;
}) {
  const { enabled, businessId, fetcher } = input;

  useEffect(() => {
    if (!enabled) return;
    // A FRESH snapshot needs nothing. A STALE one still gets refreshed, but the
    // refresh happens in the background: the sheet paints from storage either
    // way, so revalidation can never be the reason a guest waits.
    if (!isAccountCacheStale(businessId)) return;

    let cancelled = false;
    const ordersPath = businessId
      ? `/api/customer-auth/orders?businessId=${encodeURIComponent(businessId)}`
      : '/api/customer-auth/orders';

    const read = async (path: string) => {
      try {
        const res = await fetcher(path);
        return await res.json();
      } catch {
        return null;
      }
    };

    const scoped = (path: string) =>
      businessId ? `${path}?businessId=${encodeURIComponent(businessId)}` : path;

    void Promise.all([
      read(scoped('/api/customer-auth/loyalty-links')),
      read(scoped('/api/customer-auth/gift-cards')),
      read(ordersPath),
    ]).then(([links, cards, orders]) => {
      if (cancelled) return;
      // Only cache a complete snapshot. A partial one would let the panel paint
      // an empty "no gift cards" state that is merely a failed request.
      if (!links?.ok || !cards?.ok || !orders?.ok) return;
      writeAccountCache(businessId, {
        linked: links.linked || [],
        candidates: links.candidates || [],
        giftCards: cards.giftCards || [],
        orders: orders.orders || [],
      });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, businessId, fetcher]);
}
